import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { CheckCircle2, FileText, Paperclip, Upload, X } from "lucide-react";

import { createGrievance, type CreateGrievanceResponse } from "@/api/students";
import { ApiError } from "@/api/client";
import { Spinner } from "@/components/Spinner";
import { ThemeToggle } from "@/components/ThemeToggle";

const ALLOWED_EXT = ["pdf", "png", "jpg", "jpeg", "docx"] as const;
const MAX_BYTES = 200 * 1024 * 1024;

type FormValues = {
  applicationId: string;
  grievanceText: string;
};

export function SubmitTicketPage() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState<CreateGrievanceResponse | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { applicationId: "", grievanceText: "" },
  });

  function onFileChange(file: File | null) {
    if (!file) {
      setAttachment(null);
      return;
    }
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    if (!ALLOWED_EXT.includes(ext as (typeof ALLOWED_EXT)[number])) {
      toast.error(`Unsupported file type. Allowed: ${ALLOWED_EXT.join(", ")}`);
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("File exceeds 200 MB limit.");
      return;
    }
    setAttachment(file);
  }

  async function onSubmit(values: FormValues) {
    try {
      const result = await createGrievance(values.applicationId.trim(), values.grievanceText.trim(), attachment);
      setSubmitted(result);
      reset();
      setAttachment(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (attachment && !result.attachment_uploaded) {
        toast.warning("Ticket submitted, but attachment failed. Contact support with your Ticket ID.");
      } else {
        toast.success(`Ticket #${result.grievance_id} submitted.`);
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Submission failed. Please try again.";
      toast.error(msg);
    }
  }

  return (
    <div className="min-h-screen">
      <header className="flex justify-between items-center px-6 py-4 max-w-3xl mx-auto">
        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
          Maharashtra State Government
        </span>
        <ThemeToggle />
      </header>

      <main className="max-w-2xl mx-auto px-6 pb-16">
        <div className="text-center mt-6 mb-10">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">
            Ticket Redressal System
          </h1>
          <p className="mt-3 text-slate-600 dark:text-slate-400">
            Welcome! Please submit your ticket using the form below.
          </p>
        </div>

        {submitted && (
          <div className="card p-6 mb-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="text-emerald-500 mt-0.5" size={20} />
              <div className="flex-1">
                <p className="text-lg font-semibold">
                  Your ticket has been submitted successfully.
                </p>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                  Your Ticket ID is{" "}
                  <span className="font-mono font-semibold text-slate-900 dark:text-slate-100">
                    #{submitted.grievance_id}
                  </span>
                  . Our team will review your request and you will be notified of the resolution.
                </p>
                <button
                  type="button"
                  className="mt-4 btn-secondary text-xs"
                  onClick={() => setSubmitted(null)}
                >
                  Submit another ticket
                </button>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="card p-6 space-y-6" noValidate>
          <h2 className="text-2xl font-bold">Submit a New Ticket</h2>

          <div>
            <label htmlFor="applicationId" className="block text-sm font-medium mb-1">
              Your Application ID
            </label>
            <input
              id="applicationId"
              type="text"
              autoComplete="off"
              placeholder="e.g. STU2025001"
              className="input-field"
              {...register("applicationId", {
                required: "Please enter your Application ID.",
                validate: (v) => v.trim().length > 0 || "Application ID is required.",
              })}
            />
            {errors.applicationId && (
              <p className="mt-1 text-xs text-rose-500">{errors.applicationId.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="grievanceText" className="block text-sm font-medium mb-1">
              Describe your issue in detail
            </label>
            <textarea
              id="grievanceText"
              rows={6}
              placeholder="Provide as much detail as possible so we can assist you effectively."
              className="input-field resize-y min-h-[140px]"
              {...register("grievanceText", {
                required: "Please describe your issue.",
                validate: (v) => v.trim().length > 0 || "Issue description is required.",
              })}
            />
            {errors.grievanceText && (
              <p className="mt-1 text-xs text-rose-500">{errors.grievanceText.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Attach a file (optional)</label>
            {!attachment ? (
              <label
                htmlFor="attachment"
                className="flex items-center gap-3 px-4 py-3 rounded-md border border-dashed border-slate-300 dark:border-surface-border bg-slate-50 dark:bg-surface-raised/40 cursor-pointer hover:border-brand-green transition"
              >
                <Upload size={18} className="text-slate-400" aria-hidden />
                <span className="text-sm font-medium">Upload</span>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  200 MB max · PDF, PNG, JPG, JPEG, DOCX
                </span>
                <input
                  ref={fileInputRef}
                  id="attachment"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.docx"
                  className="hidden"
                  onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
                />
              </label>
            ) : (
              <div className="flex items-center justify-between px-4 py-3 rounded-md border border-slate-300 dark:border-surface-border bg-slate-50 dark:bg-surface-raised/60">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText size={18} className="text-brand-appId" aria-hidden />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{attachment.name}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {(attachment.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="p-1.5 rounded hover:bg-slate-200 dark:hover:bg-surface-border"
                  onClick={() => {
                    setAttachment(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  aria-label="Remove attachment"
                >
                  <X size={16} />
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500 dark:text-slate-400 inline-flex items-center gap-1">
              <Paperclip size={12} /> All fields except attachment are required.
            </p>
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? <Spinner label="Submitting…" /> : "Submit Ticket"}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
